export interface AuthenticatedTelegramUser {
  telegram_id: number;
  display_name: string;
  username: string;
  language_code: string;
  avatar_url?: string;
  city?: string;
  role?: string;
  status?: string;
  capabilities?: string[];
}

export interface AuthenticatedTelegramSession {
  token_type: "Bearer";
  access_token: string;
  refresh_token: string;
  access_expires_at: string;
  refresh_expires_at: string;
  user: AuthenticatedTelegramUser;
}

interface TokenResponse {
  token_type: "Bearer";
  access_token: string;
  refresh_token: string;
  access_expires_at: string;
  refresh_expires_at: string;
}

interface TelegramAuthResponse extends TokenResponse {
  authenticated: boolean;
  user: AuthenticatedTelegramUser;
}

interface ApiErrorResponse {
  error?: { code?: string };
}

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

let activeSession: AuthenticatedTelegramSession | null = null;

export class TelegramAuthError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "TelegramAuthError";
  }
}

function isTokenResponse<T extends Partial<TokenResponse>>(
  value: T | undefined,
): value is T & TokenResponse {
  return (
    value?.token_type === "Bearer" &&
    typeof value.access_token === "string" &&
    typeof value.refresh_token === "string" &&
    typeof value.access_expires_at === "string" &&
    typeof value.refresh_expires_at === "string"
  );
}

async function parseJson<T>(response: Response): Promise<T | undefined> {
  try {
    return (await response.json()) as T;
  } catch {
    return undefined;
  }
}

async function throwAuthError(response: Response, body?: ApiErrorResponse): Promise<never> {
  const code = body?.error?.code ?? "telegram_authorization_failed";
  throw new TelegramAuthError("Telegram authorization failed", code, response.status);
}

export async function authenticateTelegram(
  initData: string,
  fetcher: Fetcher = fetch,
): Promise<AuthenticatedTelegramSession> {
  if (!initData) throw new Error("Telegram initData is missing");

  const response = await fetcher("/api/v1/auth/telegram", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ init_data: initData }),
  });
  const body = await parseJson<Partial<TelegramAuthResponse> & ApiErrorResponse>(response);
  if (
    !response.ok ||
    body?.authenticated !== true ||
    !body.user ||
    !isTokenResponse(body)
  ) {
    return throwAuthError(response, body);
  }
  activeSession = {
    token_type: body.token_type,
    access_token: body.access_token,
    refresh_token: body.refresh_token,
    access_expires_at: body.access_expires_at,
    refresh_expires_at: body.refresh_expires_at,
    user: body.user,
  };
  return activeSession;
}

export async function refreshTelegramSession(fetcher: Fetcher = fetch): Promise<AuthenticatedTelegramSession> {
  if (!activeSession) throw new Error("Telegram refresh token is missing");
  const response = await fetcher("/api/v1/auth/refresh", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: activeSession.refresh_token }),
  });
  const body = await parseJson<Partial<TokenResponse> & ApiErrorResponse>(response);
  if (!response.ok || !isTokenResponse(body)) return throwAuthError(response, body);
  activeSession = { ...body, user: activeSession.user };
  return activeSession;
}

export function getAccessToken(): string | null {
  return activeSession?.access_token ?? null;
}

export function getAuthenticatedUser(): AuthenticatedTelegramUser | null {
  return activeSession?.user ?? null;
}

export function clearTelegramSession(): void {
  activeSession = null;
}

export function authenticatedRequest(init: RequestInit = {}): RequestInit {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return { ...init, credentials: init.credentials ?? "include", headers };
}
